import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SelectField } from "@/components/common";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import {
  hasParentAccess,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";
import { targetSetupFiltersStore } from "../stores/target-setup-filters.store";
import { buildPickerOptions } from "@/features/esg-dashboard-v3/utils/hierarchy-picker";
const ALL = "__all__";
const JJ_COMPANY_ID = "6936847be9d900421638fe6b";
const JJ_GROUP_FRAMEWORK = "JJ-Group";
const ALL_CATEGORIES = ["General", "Environmental", "Social", "Governance"];
const HIERARCHIES = [
  { value: "all", label: "All" },
  { value: "businessUnit", label: "Business Unit" },
  { value: "company", label: "Company" },
  { value: "site", label: "Site" },
  { value: "country", label: "Country" },
];

// Bridge between single-select UI ("__all__" or one value) and the store's
// array shape ([] = all, [value] = one).
const singleToArray = (value) => (value === ALL || !value ? [] : [value]);
const arrayToSingle = (arr) => (arr.length === 1 ? arr[0] : ALL);
const getFrameworkCode = (framework) => {
  if (framework === null || framework === undefined) return "";
  if (typeof framework === "object") {
    return String(
      framework.code ?? framework.framework ?? framework.value ?? "",
    ).trim();
  }
  return String(framework).trim();
};
const getReportingYearFrameworkCodes = (reportingYear) => [
  ...(Array.isArray(reportingYear?.frameworkCodes)
    ? reportingYear.frameworkCodes
    : []),
  ...(Array.isArray(reportingYear?.frameworks) ? reportingYear.frameworks : []),
];
const buildUniqueFrameworkCodes = (reportingYears = []) => {
  const seen = new Set();
  return (Array.isArray(reportingYears) ? reportingYears : [])
    .flatMap(getReportingYearFrameworkCodes)
    .map(getFrameworkCode)
    .filter((code) => {
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
};

const getReportingYearOrganizationIds = (
  reportingYear = {},
  companyId = "",
) => {
  if (!reportingYear) return [];
  if (reportingYear?.isAllOrganization) return [];

  const orgIds = (
    Array.isArray(reportingYear?.organizations)
      ? reportingYear.organizations
      : []
  )
    .map((org) => String(org?._id || org?.id || org?.key || "").trim())
    .filter(Boolean);

  if (reportingYear?.isCompanySelected && companyId) {
    orgIds.push(String(companyId));
  }

  return Array.from(new Set(orgIds));
};

const filterTreeByAllowedOrganizationIds = (nodes = [], allowedIds = []) => {
  const allowedSet = new Set((allowedIds || []).map((id) => String(id)));
  if (allowedSet.size === 0) return nodes;

  const visit = (items = []) => {
    return (items || [])
      .map((node) => {
        const nodeId = normalizeOrgId(node?.id || node?._id || node?.key);
        const children = visit(node?.children || []);
        const isAllowed = nodeId ? allowedSet.has(String(nodeId)) : false;

        if (isAllowed || children.length > 0) {
          return { ...node, children };
        }

        return null;
      })
      .filter(Boolean);
  };

  return visit(nodes);
};

const sortReportingYearsDescending = (reportingYears = []) =>
  [...reportingYears].sort((yearA = {}, yearB = {}) => {
    const fields = ["startYear", "endYear", "startMonth", "endMonth"];

    for (const field of fields) {
      const valueA = Number(yearA[field]) || 0;
      const valueB = Number(yearB[field]) || 0;
      if (valueA !== valueB) return valueB - valueA;
    }

    const createdAtA = yearA.createdAt
      ? new Date(yearA.createdAt).getTime()
      : 0;
    const createdAtB = yearB.createdAt
      ? new Date(yearB.createdAt).getTime()
      : 0;
    return createdAtB - createdAtA;
  });

const FilterBar = observer(
  ({ hideFilterBy = false, frameworkOptions: externalFrameworkOptions }) => {
    const { t } = useTran();
    const { filters, available, setFilter, reset, activeCount } =
      targetSetupFiltersStore;
    const { user } = useAuthStore();
    const frameworkAllSelectedRef = useRef(false);

    const yearOptions = useMemo(() => {
      const reportingYearOptions = sortReportingYearsDescending(
        available.reportingYears,
      ).map((reportingYear) => ({
        value: String(reportingYear.startYear),
        label: formatReportingYearPeriod(reportingYear),
      }));

      if (reportingYearOptions.length > 0) {
        if (reportingYearOptions.length === 1) return reportingYearOptions;
        return [{ value: ALL, label: t("All Years") }, ...reportingYearOptions];
      }

      const fallbackYearOptions = available.years
        .slice()
        .sort((a, b) => b - a)
        .map((y) => ({ value: String(y), label: String(y) }));

      if (fallbackYearOptions.length === 1) return fallbackYearOptions;

      return [{ value: ALL, label: t("All Years") }, ...fallbackYearOptions];
    }, [available.reportingYears, available.years, t]);

    const isJJCompany = String(user?.companyId || "") === JJ_COMPANY_ID;

    const frameworkOptions = useMemo(() => {
      if (
        Array.isArray(externalFrameworkOptions) &&
        externalFrameworkOptions.length > 0
      ) {
        return externalFrameworkOptions;
      }

      const byCode = new Map(
        available.frameworkObjects.map((f) => [f.code, f.name]),
      );
      const selectedYear =
        filters.years.length === 1 ? Number(filters.years[0]) : null;
      const frameworkCodes = Number.isFinite(selectedYear)
        ? buildUniqueFrameworkCodes(
            available.reportingYears.filter(
              (reportingYear) =>
                Number(reportingYear?.startYear) === selectedYear,
            ),
          )
        : buildUniqueFrameworkCodes(available.reportingYears);
      const effectiveFrameworkCodes =
        frameworkCodes.length > 0 ? frameworkCodes : available.frameworks;
      const realOptions = effectiveFrameworkCodes.map((code) => ({
        value: code,
        label: byCode.get(code) ?? code,
      }));
      const options = isJJCompany
        ? [
            ...(realOptions.some(
              (option) => option.value === JJ_GROUP_FRAMEWORK,
            )
              ? []
              : [{ value: JJ_GROUP_FRAMEWORK, label: JJ_GROUP_FRAMEWORK }]),
            ...realOptions,
          ]
        : realOptions;

      if (options.length === 1) return options;

      return [{ value: ALL, label: t("All Frameworks") }, ...options];
    }, [
      available.frameworkObjects,
      available.frameworks,
      available.reportingYears,
      externalFrameworkOptions,
      filters.years,
      isJJCompany,
      t,
    ]);

    const categoryOptions = useMemo(() => {
      const allowed = hasParentAccess(user)
        ? ALL_CATEGORIES
        : ALL_CATEGORIES.filter((c) => c !== "General");
      return [
        { value: ALL, label: t("All Categories") },
        ...allowed.map((c) => ({ value: c, label: t(c) })),
      ];
    }, [user, t]);

    const hierarchyOptions = useMemo(
      () =>
        HIERARCHIES.filter(
          (h) =>
            h.value === "all" ||
            buildPickerOptions(available.orgTree, h.value).length > 0,
        ).map((h) => ({ value: h.value, label: t(h.label) })),
      [available.orgTree, t],
    );

    useEffect(() => {
      if (hideFilterBy) return;
      if (!hierarchyOptions.some((o) => o.value === filters.hierarchy)) {
        setFilter("hierarchy", "all");
      }
    }, [hideFilterBy, hierarchyOptions, filters.hierarchy, setFilter]);

    const selectedYear = arrayToSingle(filters.years.map(String));
    const selectedReportingYear = useMemo(() => {
      if (!selectedYear || selectedYear === ALL) return null;
      return (
        (available.reportingYears || []).find(
          (reportingYear) =>
            String(reportingYear?.startYear) === String(selectedYear),
        ) || null
      );
    }, [available.reportingYears, selectedYear]);

    const allowedOrganizationIds = useMemo(() => {
      if (!selectedReportingYear) return [];
      return getReportingYearOrganizationIds(
        selectedReportingYear,
        user?.companyId || "",
      );
    }, [selectedReportingYear, user?.companyId]);

    const scopedOrgTree = useMemo(() => {
      if (!selectedReportingYear) return available.orgTree || [];
      return filterTreeByAllowedOrganizationIds(
        available.orgTree || [],
        allowedOrganizationIds,
      );
    }, [allowedOrganizationIds, available.orgTree, selectedReportingYear]);

    useEffect(() => {
      const realYearOptions = yearOptions.filter(
        (option) => option.value !== ALL,
      );

      // Year options haven't loaded yet — there's nothing to validate a
      // selection against, so don't prune it out from under a URL-applied
      // filter that arrived before this data did.
      if (realYearOptions.length === 0) return;

      if (realYearOptions.length === 1) {
        const onlyYear = Number(realYearOptions[0].value);
        if (filters.years.length !== 1 || filters.years[0] !== onlyYear) {
          setFilter("years", [onlyYear]);
        }
        return;
      }

      if (filters.years.length === 0) return;

      const availableYears = new Set(
        realYearOptions.map((option) => option.value),
      );
      const selectedYears = filters.years.map(String);
      if (!selectedYears.every((year) => availableYears.has(year))) {
        setFilter("years", []);
      }
    }, [filters.years, setFilter, yearOptions]);

    useEffect(() => {
      if (!selectedReportingYear) return;
      // An empty allowed list means no restriction (e.g. a reporting year
      // marked isAllOrganization) — same convention as
      // filterTreeByAllowedOrganizationIds above. Only strip a selection
      // when there's an actual curated list it doesn't belong to.
      if (allowedOrganizationIds.length === 0) return;

      const nextOrgIds = (filters.orgIds || []).filter((id) => {
        const normalizedId = String(id || "").trim();
        if (!normalizedId) return false;
        return allowedOrganizationIds.includes(normalizedId);
      });

      if (nextOrgIds.length !== (filters.orgIds || []).length) {
        setFilter("orgIds", nextOrgIds);
      }
    }, [
      allowedOrganizationIds,
      filters.orgIds,
      selectedReportingYear,
      setFilter,
    ]);

    useEffect(() => {
      if (isJJCompany) {
        if (
          filters.frameworks.length !== 1 ||
          filters.frameworks[0] !== JJ_GROUP_FRAMEWORK
        ) {
          setFilter("frameworks", [JJ_GROUP_FRAMEWORK]);
        }
        return;
      }

      const realFrameworkOptions = frameworkOptions.filter(
        (option) => option.value !== ALL,
      );
      if (realFrameworkOptions.length === 1) {
        const onlyFramework = realFrameworkOptions[0].value;
        if (
          filters.frameworks.length !== 1 ||
          filters.frameworks[0] !== onlyFramework
        ) {
          setFilter("frameworks", [onlyFramework]);
        }
        return;
      }

      if (filters.frameworks.length === 0) return;

      const availableFrameworks = new Set(
        realFrameworkOptions.map((option) => option.value),
      );
      if (
        !filters.frameworks.every((framework) =>
          availableFrameworks.has(framework),
        )
      ) {
        setFilter("frameworks", []);
      }
    }, [filters.frameworks, frameworkOptions, isJJCompany, setFilter]);

    const pickerTree = useMemo(
      () => buildPickerOptions(scopedOrgTree, filters.hierarchy),
      [scopedOrgTree, filters.hierarchy],
    );
    const selectedFramework = arrayToSingle(filters.frameworks);

    return (
      <div className="flex flex-wrap items-end gap-3 bg-muted/40 rounded-lg p-3">
        {!hideFilterBy && (
          <SelectField
            label={t("Filter By")}
            options={hierarchyOptions}
            value={filters.hierarchy}
            onValueChange={(v) => setFilter("hierarchy", v)}
            sortOptions={false}
            className="w-full sm:w-40"
          />
        )}

        <SelectField
          label={t("Reporting Year")}
          options={yearOptions}
          value={selectedYear}
          onValueChange={(value) =>
            setFilter("years", singleToArray(value).map(Number))
          }
          sortOptions={false}
          className="w-full sm:w-72"
        />

        <div className="w-full sm:w-56 space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            {t("Organization")}
          </label>
          <OrgTreeSelect
            tree={pickerTree}
            value={filters.orgIds}
            onChange={(ids) => setFilter("orgIds", ids)}
            multiSelect
            showBranchSelectAll
            allowOrgs={allowedOrganizationIds}
            branchSelectLevels={["parent_company", "level_1"]}
            placeholder={t("All Organizations")}
            className="w-full"
          />
        </div>

        <SelectField
          label={t("Framework")}
          options={frameworkOptions}
          value={selectedFramework}
          onValueChange={(value) => {
            frameworkAllSelectedRef.current = value === ALL;
            setFilter("frameworks", singleToArray(value));
          }}
          sortOptions={false}
          className="w-full sm:w-56"
        />
        <SelectField
          label={t("Category")}
          options={categoryOptions}
          value={arrayToSingle(filters.categories)}
          onValueChange={(v) => setFilter("categories", singleToArray(v))}
          sortOptions={false}
          className="w-full sm:w-40"
        />

        {activeCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              frameworkAllSelectedRef.current = false;
              reset();
            }}
            className="gap-1.5"
          >
            <X size={14} />
            {t("Clear")}
            <Badge
              variant="secondary"
              className="ml-1 px-1.5 py-0 text-[10px] font-semibold"
            >
              {activeCount}
            </Badge>
          </Button>
        )}
      </div>
    );
  },
);

export default FilterBar;
